import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  useGetProductLawQuestionnaire,
  useSetProductLawQuestionnaireAnswers,
  type LawQuestionnaireQuestionGroup,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { V2LawApplicabilityQuestionnaireGroups } from './V2LawApplicabilityQuestionnaireGroups'
import {
  buildDraftFromQuestions,
  getLawQuestionnaireStatusBanner,
  getLiveLawQuestionnaireStatus,
  getPendingQuestionnaireNoticeKey,
  getPreferredLawQuestionnaireGroupFromSections,
  getVisibleLawQuestionnaireQuestions,
  groupLawQuestionnaireQuestions,
  type LawQuestionnaireAnswerDraft,
} from '../utils/lawApplicabilityQuestionnaire'
import {
  getLivePedQuestionnaireStatus,
  mergePedQuestionnaireAnswers,
  pedScopeAndExclusionsComplete,
  readPedFieldsFromAnswers,
  type PedApplicabilityFields,
} from '../utils/pedApplicabilityQuestionnaire'
import { V2PedApplicabilityConfirmationFields } from './V2PedApplicabilityConfirmationFields'
import {
  localizeLawQuestionnaireQuestions,
  useLawQuestionnaireTranslation,
} from '../utils/localizeLawQuestionnaireQuestion'

type V2LawApplicabilityQuestionnaireSheetProps = {
  productId: string
  law: ProductLawKey
  lawLabel: string
  open: boolean
  disabled: boolean
  /** When true, only unanswered questions are shown (post-discover grilling). */
  pendingOnly?: boolean
  onOpenChange: (open: boolean) => void
}

export function V2LawApplicabilityQuestionnaireSheet({
  productId,
  law,
  lawLabel,
  open,
  disabled,
  pendingOnly = false,
  onOpenChange,
}: V2LawApplicabilityQuestionnaireSheetProps) {
  const { t, i18n } = useLawQuestionnaireTranslation(law)
  const { data, isLoading, isError } = useGetProductLawQuestionnaire(
    productId,
    law,
    open
  )
  const { mutateAsync: saveAnswers, isPending } =
    useSetProductLawQuestionnaireAnswers(productId, law)
  const [draft, setDraft] = useState<LawQuestionnaireAnswerDraft>({})
  const [pedFields, setPedFields] = useState<PedApplicabilityFields>({
    equipmentKind: null,
    classification: null,
  })
  const [selectedGroup, setSelectedGroup] =
    useState<LawQuestionnaireQuestionGroup | null>(null)

  useEffect(() => {
    if (!open || data == null) {
      return
    }
    const nextDraft = buildDraftFromQuestions(data.questions)
    setPedFields(readPedFieldsFromAnswers(data.applicability?.answers))
    const visible = getVisibleLawQuestionnaireQuestions(
      data.questions,
      nextDraft,
      pendingOnly
    )
    setDraft(nextDraft)
    setSelectedGroup(
      getPreferredLawQuestionnaireGroupFromSections(
        law,
        visible.questions,
        nextDraft,
        data.categoryRule,
        data.requiresCompletePackBeforeVerdict
      )
    )
  }, [open, data, pendingOnly, law])

  const questionsForBanner = useMemo(() => {
    if (data == null) {
      return []
    }
    return localizeLawQuestionnaireQuestions(
      t,
      law,
      data.questions,
      i18n.language
    )
  }, [data, t, law, i18n.language])

  const { questions: visibleQuestions, reviewAllAnswered, unansweredCount } =
    useMemo(() => {
      if (data == null) {
        return {
          questions: [],
          reviewAllAnswered: false,
          unansweredCount: 0,
        }
      }
      return getVisibleLawQuestionnaireQuestions(
        data.questions,
        draft,
        pendingOnly
      )
    }, [data, draft, pendingOnly])

  useEffect(() => {
    if (visibleQuestions.length === 0) {
      return
    }
    const grouped = groupLawQuestionnaireQuestions(visibleQuestions)
    const selectedGroupIsVisible = grouped.some((entry) => {
      return entry.group === selectedGroup
    })
    if (selectedGroupIsVisible) {
      return
    }
    setSelectedGroup(
      getPreferredLawQuestionnaireGroupFromSections(
        law,
        visibleQuestions,
        draft,
        data?.categoryRule ?? null,
        data?.requiresCompletePackBeforeVerdict ?? false
      )
    )
  }, [
    visibleQuestions,
    selectedGroup,
    draft,
    data?.categoryRule,
    data?.requiresCompletePackBeforeVerdict,
    law,
  ])

  async function handleSave() {
    if (data == null) {
      return
    }

    try {
      const answersPayload =
        law === 'ped'
          ? mergePedQuestionnaireAnswers(draft, pedFields)
          : draft
      await saveAnswers({
        answers: answersPayload as Record<string, boolean | null | string>,
      })
      if (pendingOnly) {
        toast.success(t('v2.applicability.pendingQuestions.saveSuccess'))
      }
      onOpenChange(false)
    } catch {
      toast.error(t('v2.applicability.questionnaire.errors.saveFailed'))
    }
  }

  function handleSelectAnswer(questionKey: string, value: boolean | null) {
    setDraft((previous) => ({
      ...previous,
      [questionKey]: value,
    }))
  }

  let titleKey = 'v2.applicability.questionnaire.title'
  if (pendingOnly) {
    titleKey = 'v2.applicability.pendingQuestions.sheetTitle'
  } else if (disabled) {
    titleKey = 'v2.applicability.questionnaire.viewTitle'
  }

  let descriptionKey = 'v2.applicability.questionnaire.description'
  if (pendingOnly) {
    descriptionKey = 'v2.applicability.pendingQuestions.description'
  } else if (disabled) {
    descriptionKey = 'v2.applicability.questionnaire.viewDescription'
  }

  const showLoadingSkeleton = isLoading
  const showLoadError = isError
  const showQuestionList = data != null && visibleQuestions.length > 0
  const liveStatus =
    data != null
      ? law === 'ped'
        ? getLivePedQuestionnaireStatus(draft, pedFields)
        : getLiveLawQuestionnaireStatus(
            data.questions,
            draft,
            data.categoryRule,
            data.requiresCompletePackBeforeVerdict
          )
      : 'UNDECIDED'

  const showPedConfirmation =
    law === 'ped' && pedScopeAndExclusionsComplete(draft)

  const statusBanner =
    data != null
      ? getLawQuestionnaireStatusBanner(
          law,
          liveStatus,
          questionsForBanner,
          draft,
          data.categoryRule,
          data.requiresCompletePackBeforeVerdict
        )
      : null

  let noticeKey: ReturnType<typeof getPendingQuestionnaireNoticeKey> = null
  if (data != null) {
    noticeKey = getPendingQuestionnaireNoticeKey(
      pendingOnly,
      unansweredCount,
      liveStatus
    )
  }
  const showRemainingCountBadge =
    pendingOnly && !reviewAllAnswered && unansweredCount > 0
  const allowUnsetAnswers = !pendingOnly || reviewAllAnswered
  const saveDisabled = disabled || isPending || isLoading || isError
  let remainingCount: number | null = null
  if (showRemainingCountBadge) {
    remainingCount = unansweredCount
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='top-0 left-0 flex h-[100dvh] max-h-[100dvh] w-screen max-w-none! translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:max-w-none! data-ending-style:scale-100 data-starting-style:scale-100'>
        <DialogHeader className='shrink-0 border-b px-6 py-4 pe-14 text-start'>
          <DialogTitle>{t(titleKey, { law: lawLabel })}</DialogTitle>
          <DialogDescription className='sr-only'>
            {t(descriptionKey)}
          </DialogDescription>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col'>
          {showLoadingSkeleton && (
            <div className='space-y-3 p-6'>
              <Skeleton className='h-16 w-full' />
              <Skeleton className='h-16 w-full' />
            </div>
          )}
          {showLoadError && (
            <p className='p-6 text-sm text-destructive'>
              {t('v2.applicability.questionnaire.errors.loadFailed')}
            </p>
          )}
          {statusBanner != null && showQuestionList && (
            <div className='border-b px-6 py-3 text-sm leading-relaxed'>
              <p>
                <span className='font-semibold'>
                  {t(statusBanner.leadKey)}
                </span>{' '}
                {t(statusBanner.bodyKey, statusBanner.bodyParams)}
              </p>
            </div>
          )}
          {noticeKey != null && (
            <p className='border-b px-6 py-3 text-sm text-muted-foreground'>
              {t(noticeKey)}
            </p>
          )}
          {showQuestionList && (
            <V2LawApplicabilityQuestionnaireGroups
              law={law}
              questions={visibleQuestions}
              draft={draft}
              categoryRule={data?.categoryRule ?? null}
              requiresCompletePackBeforeVerdict={
                data?.requiresCompletePackBeforeVerdict ?? false
              }
              selectedGroup={selectedGroup}
              remainingCount={remainingCount}
              disabled={disabled}
              isPending={isPending}
              allowUnset={allowUnsetAnswers}
              onSelectGroup={setSelectedGroup}
              onSelectAnswer={handleSelectAnswer}
            />
          )}
          {showPedConfirmation && (
            <div className="border-t px-6 py-4">
              <V2PedApplicabilityConfirmationFields
                fields={pedFields}
                disabled={disabled}
                showClassification
                onChange={setPedFields}
              />
            </div>
          )}
        </div>

        <DialogFooter className='shrink-0 border-t px-6 py-4 sm:justify-end'>
          <Button
            type='button'
            disabled={saveDisabled}
            onClick={() => {
              void handleSave()
            }}
          >
            {t('v2.applicability.questionnaire.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
